// T3 (V218) — REPARTO GUIADO por talla (Boutique). El tenant cuenta físicamente cada
// talla de cada sucursal; al aplicar, el pool sin talla sale y cada talla queda en su
// conteo. Es la única vía para activar una talla en un producto con existencia: la talla
// nueva llega como `newVariants` y no se guarda hasta repartir.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import {
  type NewVariantDraft, type RepartoBranch, type RepartoView, branchHasStock, buildPayload, initialCounts, summarize,
} from './repartoLogic';

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50';

/** Decision 5 — "venta sin talla, asígnala": cada renglón viejo cobrado sin talla en una
 *  sucursal ya repartida queda pendiente hasta asignarlo a la talla que se vendió. */
export function PendientesList({ branch, onAssign, busy = false }: {
  branch: RepartoBranch; onAssign: (movementId: string, variantId: string) => void; busy?: boolean;
}) {
  const [pick, setPick] = useState<Record<string, string>>({});
  const items = branch.pendientes ?? [];
  if (items.length === 0) return null;
  const sizes = branch.sizes.filter((z) => z.isActive);
  return (
    <div className="mb-2 space-y-1 rounded-lg bg-amber-50 px-3 py-2">
      {items.map((p) => (
        <div key={p.movementId} className="flex flex-wrap items-center gap-2 text-xs text-amber-900" data-pendiente={p.movementId}>
          <span><b>Venta sin talla, asígnala</b> · {p.qty} pza · {new Date(p.createdAt).toLocaleString('es-MX')}</span>
          <select aria-label="Talla vendida" className="rounded-lg border border-amber-300 bg-white px-2 py-0.5"
            value={pick[p.movementId] ?? ''} onChange={(e) => setPick((m) => ({ ...m, [p.movementId]: e.target.value }))}>
            <option value="">¿Qué talla fue?</option>
            {sizes.map((z) => <option key={z.variantId} value={z.variantId}>{z.name}</option>)}
          </select>
          <button type="button" className={btnGhost} disabled={busy || !pick[p.movementId]}
            onClick={() => onAssign(p.movementId, pick[p.movementId])}>Asignar</button>
        </div>
      ))}
    </div>
  );
}

export function RepartoBranchTable({ branch, newVariants, counts, onCount }: {
  branch: RepartoView['branches'][number]; newVariants: NewVariantDraft[];
  counts: Record<string, string>; onCount: (key: string, v: string) => void;
}) {
  const s = summarize(branch, counts, newVariants.length);
  return (
    <div className="space-y-2">
      {branch.pool !== 0 && (
        <p className={`rounded-lg px-3 py-1.5 text-xs ${branch.mode === 'desglosado' ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-600'}`}>
          {branch.mode === 'desglosado'
            ? <>Existencia <b>sin talla</b> (no se ve en las tallas): <b className="tabular-nums">{branch.pool}</b>. Al repartir sale y queda lo que cuentes.</>
            : <>Existencia compartida (sin repartir): <b className="tabular-nums">{branch.pool}</b>. Cuenta cuántas hay de cada talla.</>}
        </p>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr><th className="py-1">Talla</th><th className="py-1 text-right">Sistema</th><th className="py-1 text-right">Conteo físico</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {branch.sizes.map((z) => (
            <tr key={z.variantId} data-negative={z.negative ? 'true' : undefined}>
              <td className="py-1.5 text-slate-700">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">{z.name}</span>
                {!z.isActive && <span className="ml-1 text-[10px] text-slate-400">inactiva</span>}
                {z.negative && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">negativa · cuéntala</span>}
              </td>
              <td className={`py-1.5 text-right tabular-nums ${z.negative ? 'font-semibold text-rose-600' : 'text-slate-500'}`}>
                {branch.mode === 'pool' ? '—' : z.onHand}
              </td>
              <td className="py-1.5 text-right">
                <input aria-label={`Conteo ${z.name} ${branch.branchName}`} inputMode="numeric"
                  className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm tabular-nums"
                  value={counts[z.variantId] ?? ''} placeholder="0" onChange={(e) => onCount(z.variantId, e.target.value)} />
              </td>
            </tr>
          ))}
          {newVariants.map((nv, i) => (
            <tr key={`new:${i}`}>
              <td className="py-1.5 text-slate-700">
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">{nv.name}</span>
                <span className="ml-1 text-[10px] text-emerald-700">nueva</span>
              </td>
              <td className="py-1.5 text-right text-slate-400">—</td>
              <td className="py-1.5 text-right">
                <input aria-label={`Conteo ${nv.name} ${branch.branchName}`} inputMode="numeric"
                  className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm tabular-nums"
                  value={counts[`new:${i}`] ?? ''} placeholder="0" onChange={(e) => onCount(`new:${i}`, e.target.value)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-500">
        Sistema: <b className="tabular-nums">{s.before}</b>
        {s.after != null
          ? <> · Contado: <b className="tabular-nums">{s.after}</b> · Diferencia: <b className={`tabular-nums ${s.diff! < 0 ? 'text-rose-600' : s.diff! > 0 ? 'text-emerald-700' : ''}`}>{s.diff! > 0 ? `+${s.diff}` : s.diff}</b></>
          : <> · faltan {s.missing} talla(s) por contar</>}
      </p>
    </div>
  );
}

export function StockRepartoModal({ productId, newVariants = [], focusBranchId, onClose, onSaved }: {
  productId: string; newVariants?: NewVariantDraft[]; focusBranchId?: string | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [view, setView] = useState<RepartoView | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, Record<string, string>>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Una llave por apertura: reintento/doble clic = no-op en el backend.
  const [repartoKey] = useState(() => crypto.randomUUID());
  const activating = newVariants.length > 0;

  useEffect(() => {
    let cancelled = false;
    api(`/boutique/products/${productId}/reparto`).then((v: any) => {
      if (cancelled) return;
      const rv = v as RepartoView;
      setView(rv);
      const init: Record<string, Record<string, string>> = {};
      for (const b of rv.branches) init[b.branchId] = initialCounts(b, newVariants.length);
      setCounts(init);
      // Al activar tallas: TODAS las sucursales con existencia van obligatorias. Si no, la
      // sucursal activa (o, sin ella, las que tienen existencia).
      const withStock = rv.branches.filter(branchHasStock).map((b) => b.branchId);
      setSelected(activating ? withStock : focusBranchId ? [focusBranchId] : withStock);
    }).catch((e: any) => { if (!cancelled) setErr(e?.body?.message || e?.message || 'Error'); });
    return () => { cancelled = true; };
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mandatory = useMemo(
    () => new Set(activating && view ? view.branches.filter(branchHasStock).map((b) => b.branchId) : []),
    [activating, view]);
  const incomplete = view
    ? view.branches.filter((b) => selected.includes(b.branchId))
        .some((b) => summarize(b, counts[b.branchId] ?? {}, newVariants.length).missing > 0)
    : true;

  const assign = async (movementId: string, variantId: string) => {
    const branchId = view?.branches.find((x) => (x.pendientes ?? []).some((p) => p.movementId === movementId))?.branchId;
    setBusy(true); setErr(null);
    try {
      const rv = await api(`/boutique/products/${productId}/reparto/pendientes/${movementId}/asignar`, {
        method: 'POST', body: { variantId },
      }) as RepartoView;
      setView(rv);
      // Los saldos de esa sucursal cambiaron: re-prellena SOLO su captura.
      const b = rv.branches.find((x) => x.branchId === branchId);
      if (b) setCounts((c) => ({ ...c, [b.branchId]: initialCounts(b, newVariants.length) }));
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setCount = (branchId: string, key: string, v: string) =>
    setCounts((c) => ({ ...c, [branchId]: { ...(c[branchId] ?? {}), [key]: v } }));

  const submit = async () => {
    if (!view) return;
    if (selected.length === 0) { setErr('Elige al menos una sucursal para repartir.'); return; }
    if (incomplete) { setErr('Cuenta cada talla de las sucursales elegidas (pon 0 si no hay).'); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/boutique/products/${productId}/reparto`, {
        method: 'POST', body: buildPayload(repartoKey, view, selected, counts, newVariants),
      });
      onSaved();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Repartir por talla{view ? ` — ${view.productName}` : ''}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <p className="text-[12px] text-slate-500">
            Cuenta físicamente cuántas piezas hay de cada talla. Al aplicar, la existencia sin talla sale y cada talla
            queda exactamente en lo que contaste{activating ? '. La talla nueva se guarda junto con el reparto' : ''}.
          </p>
          {!view && !err && <p className="text-sm text-slate-400">Cargando…</p>}
          {view?.branches.map((b) => (
            <section key={b.branchId} className="rounded-xl border border-slate-200 p-3">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-800">
                <input type="checkbox" checked={selected.includes(b.branchId)} disabled={mandatory.has(b.branchId)}
                  onChange={() => toggle(b.branchId)} />
                {b.branchName}
                <span className="text-[11px] font-normal text-slate-400">{b.mode === 'pool' ? 'sin repartir' : 'por talla'}</span>
                {mandatory.has(b.branchId) && <span className="text-[11px] font-normal text-amber-700">obligatoria: tiene existencia</span>}
              </label>
              <PendientesList branch={b} busy={busy} onAssign={assign} />
              {selected.includes(b.branchId) && (
                <RepartoBranchTable branch={b} newVariants={newVariants} counts={counts[b.branchId] ?? {}}
                  onCount={(k, v) => setCount(b.branchId, k, v)} />
              )}
            </section>
          ))}
          {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-6 py-3">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="button" className={btnPrimary} disabled={busy || !view || incomplete || selected.length === 0} onClick={submit}>
            {busy ? 'Repartiendo…' : 'Aplicar reparto'}
          </button>
        </div>
      </div>
    </div>
  );
}
